import {
  ADMIN_METRIC_ACCENT_TEXT,
  ADMIN_PALETTE,
} from '@/config/adminChartTheme';
import React, { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartCard from '../ChartCard';
import { fetchRoasTrend } from '../../../services/campaignAnalyticsService';

interface RoasPoint {
  label: string;
  roas: number;
  gmv: number;
  cashbackPaid: number;
}

interface RoasTrendChartProps {
  refreshToken?: number;
}

const RoasTrendChart: React.FC<RoasTrendChartProps> = ({ refreshToken }) => {
  const [data, setData] = useState<RoasPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRoasTrend()
      .then((d) => setData(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
  }, [refreshToken]);

  const avgRoas = data.length
    ? Math.round((data.reduce((s, d) => s + d.roas, 0) / data.length) * 100) /
      100
    : 0;

  const series = [{ name: 'ROAS', data: data.map((d) => d.roas) }];

  const options: ApexOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: 'Inter, sans-serif',
      background: 'transparent',
      animations: { enabled: true, easing: 'easeinout', speed: 600 },
    },
    colors: [ADMIN_PALETTE.primary],
    plotOptions: {
      bar: {
        borderRadius: 5,
        columnWidth: '50%',
        distributed: false,
      },
    },
    xaxis: {
      categories: data.map((d) => d.label),
      labels: {
        style: { colors: '#9B919D', fontSize: '10px' },
        rotate: 0,
        hideOverlappingLabels: true,
        trim: true,
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#9B919D', fontSize: '11px' },
        formatter: (v) => `${v}x`,
      },
    },
    grid: { borderColor: '#F0F0F0', strokeDashArray: 4 },
    tooltip: {
      theme: 'light',
      y: { formatter: (v) => `${v}x ROAS` },
    },
    dataLabels: { enabled: false },
  };

  return (
    <ChartCard
      title="ROAS Trend"
      subtitle="Monthly return on ad spend"
      loading={loading}
      actions={
        avgRoas > 0 ? (
          <div className="text-right">
            <p className="text-xs text-[#9B919D]">Avg ROAS</p>
            <p className={`text-lg font-bold ${ADMIN_METRIC_ACCENT_TEXT}`}>
              {avgRoas}x
            </p>
          </div>
        ) : undefined
      }
    >
      <div style={{ height: 260 }}>
        {data.length > 0 ? (
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height="100%"
            width="100%"
          />
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-[#9B919D] text-sm">No ROAS data available</p>
          </div>
        )}
      </div>
    </ChartCard>
  );
};

export default RoasTrendChart;
